import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import { stoneBiome } from '../biomeArchetypes';
import { JOURNEY_DEPTH_BACKDROPS, JOURNEY_DEPTH_HEIGHTS } from '../../../game/effects/atmosphere/silhouettes/journeyDepthBackdrops';

/**
 * Lanternroot Hollow — deep subterranean root cavern winding beneath Lanternwood.
 *
 * Distinct from the surface forest: pitch-dark earthen abyss gradient, pale
 * bioluminescent green spores and drifting amber motes, dim emerald light filtering
 * between colossal gnarled roots, pulsating fungal walls, and massive column/ridge
 * silhouettes of tangled roots and cavern walls.
 */
export const LANTERNROOT_HOLLOW = stoneBiome({
  id: 'lanternroot_hollow',
  sky: { from: 0x030a08, to: 0x0e2218, bands: 6 },
  motes: {
    colors: [0x67a879, 0x82c896, 0xc89848],
    count: 12,
    sizeMin: 3,
    sizeMax: 8,
    driftPx: 18,
    periodMinMs: 8000,
    periodMaxMs: 16000,
    alphaMin: 0.15,
    alphaMax: 0.45,
  },
  rays: {
    color: 0x5a8668,
    count: 1,
    widthMin: 70,
    widthMax: 140,
    height: 480,
    tiltDeg: 4,
    periodMinMs: 15000,
    periodMaxMs: 24000,
    alphaMin: 0.03,
    alphaMax: 0.08,
  },
  glow: {
    blobs: [
      { color: 0x528860, atX: 0.32, atY: 0.88, size: 400, alpha: 0.08 },
      { color: 0x987236, atX: 0.72, atY: 0.80, size: 280, alpha: 0.06 },
    ],
    breathe: { periodMs: 7000, min: 0.72, max: 1.20 },
  },
  lightField: {
    palette: [0x528860, 0x30523e, 0x7c6232],
    count: 6,
    sizeMin: 70,
    sizeMax: 160,
    alphaMin: 0.02,
    alphaMax: 0.08,
    driftRadius: 35,
    periodMinMs: 11000,
    periodMaxMs: 19000,
  },
  flicker: {
    color: 0x67a879,
    size: 170,
    baseAlpha: 0.07,
    flickerAmp: 0.04,
    hzMin: 1.6,
    hzMax: 3.2,
    anchors: [{ atX: 0.25, atY: 0.86 }, { atX: 0.70, atY: 0.82 }],
  },
  extraLayers: [
    {
      kind: 'bioluminescence',
      color: 0x67a879,
      count: 6,
      sizeMin: 70,
      sizeMax: 150,
      alphaMin: 0.08,
      alphaMax: 0.45,
      bandYMin: 0.45,
      bandYMax: 0.92,
      driftPx: 14,
      periodMinMs: 4000,
      periodMaxMs: 8000,
    },
    {
      kind: 'parallax',
      shape: 'stalactites',
      plane: 'ceiling',
      color: 0x050f0a,
      speed: 0.03,
      y: 0,
      height: 680,
      alpha: 0.82,
      detail: 1.0,
    },
  ],
  parallax: [
    { svg: JOURNEY_DEPTH_BACKDROPS.lanternroot, shape: 'columns', color: 0x1e3c2e, speed: 0.04, height: JOURNEY_DEPTH_HEIGHTS.lanternroot, alpha: 0.44, detail: 0.85, sway: { ampPx: 6, periodMs: 36000 } },
    { svg: OTHERWORLD_BACKDROPS.roots.mid, shape: 'ridge', color: 0x0c1912, speed: 0.10, height: 560, alpha: 0.84, detail: 1.0 },
    { svg: OTHERWORLD_BACKDROPS.roots.near, shape: 'ridge', color: 0x050f0a, speed: 0.18, height: 320, alpha: 0.95, detail: 0.9 },
  ],
  fog: {
    color: 0x243e30,
    bands: 3,
    alphaMin: 0.05,
    alphaMax: 0.12,
    driftPx: 30,
    periodMinMs: 12000,
    periodMaxMs: 20000,
    yMin: 0.58,
    yMax: 0.94,
    sizeMin: 200,
    sizeMax: 420,
  },
  closer: {
    vignette: { color: 0x010503, edgeAlpha: 0.44, washColor: 0x0d1e16, washAlpha: 0.05 },
    grade: { top: 0x2c4636, bottom: 0x09160f, strength: 0.16, blend: 'normal' },
  },
});
